/**
 * In-page Spine / Live2D engine (no iframe).
 * Loads the vendor runtime matching asset.runtime into the host element.
 *
 * Concurrent players that need different PIXI majors still conflict on
 * window.PIXI — destroy() before switching runtimes, or keep one visible player.
 */

import { staticUrl } from '../config/static-assets';

export type SpineRuntime = '1.6.2' | '2.0.5' | '3.7' | '3.8' | '4.1' | string;

export type SpinePlayerAsset = {
  json?: string;
  skel?: string;
  atlas: string;
  runtime: SpineRuntime;
  scale?: number;
  referenceHeight?: number;
  framing?: {
    targetHeight?: number;
    maxZoom?: number;
    offsetX?: number;
    offsetY?: number;
  };
  coverHeight?: boolean;
  bannerSide?: 'left' | 'right' | 'center';
  centerGap?: number;
  bannerInnerRatio?: number;
  bannerOuterRatio?: number;
  hiddenSlots?: string[];
  skin?: string;
};

export type SpineEngineCallbacks = {
  onReady?: () => void;
  onError?: () => void;
  onAnimations?: (animations: string[]) => void;
};

declare global {
  interface Window {
    PIXI?: any;
    spine?: any;
    HeroPortraitLayout?: {
      measure: (slots: unknown[], bounds: unknown) => unknown;
      layout: (
        w: number,
        h: number,
        bounds: unknown,
        portraitBounds: unknown,
        framing: unknown,
      ) => { scale: number; x: number; y: number };
    };
  }
}

const scriptCache = new Map<string, Promise<void>>();
const styleCache = new Map<string, Promise<void>>();
const activeEngines = new Set<SpineEngine>();

function loadScript(src: string) {
  const url = staticUrl(src);
  const cached = scriptCache.get(url);
  if (cached) return cached;
  const promise = new Promise<void>((resolve, reject) => {
    const element = document.createElement('script');
    element.src = url;
    element.onload = () => resolve();
    element.onerror = () => reject(new Error(`Failed to load ${url}`));
    document.head.appendChild(element);
  });
  scriptCache.set(url, promise);
  return promise;
}

function loadStylesheet(src: string) {
  const url = staticUrl(src);
  const cached = styleCache.get(url);
  if (cached) return cached;
  const promise = new Promise<void>((resolve, reject) => {
    const element = document.createElement('link');
    element.rel = 'stylesheet';
    element.href = url;
    element.onload = () => resolve();
    element.onerror = () => reject(new Error(`Failed to load ${url}`));
    document.head.appendChild(element);
  });
  styleCache.set(url, promise);
  return promise;
}

function catalogAssetUrl(path: string | undefined, catalogCdnBase: string) {
  if (!path) return path;
  const prefix = '/api/catalog-assets/';
  return catalogCdnBase && path.startsWith(prefix)
    ? `${catalogCdnBase}/${path.slice(prefix.length)}`
    : path;
}

const ALLOWED_CATALOG_CDN_HOSTS = new Set([
  'cdnimg-v2.gamekee.com',
  'cdnimg-test.gamekee.com',
]);

function allowed(path: string | undefined, catalogCdnBase: string) {
  if (typeof path !== 'string' || path.includes('..') || path.includes('\\')) {
    return false;
  }
  if (path.startsWith('/assets/') || path.startsWith('/catalog-source/assets/')) {
    return true;
  }
  if (
    /^\/api\/catalog-source-assets\/arcanatactics_(?:illust_spine|spine)\/[0-9]+\/[A-Za-z0-9_-]+\.(?:json|atlas|png)$/.test(
      path,
    )
  ) {
    return true;
  }
  try {
    const url = new URL(path, location.href);
    if (!url.pathname.startsWith('/wiki/')) return false;
    // 生产图床与 env 解析兜底：cdnimg-v2 / test 均允许
    if (ALLOWED_CATALOG_CDN_HOSTS.has(url.host)) return true;
    return (
      Boolean(catalogCdnBase) &&
      url.host === new URL(catalogCdnBase, location.href).host
    );
  } catch {
    return false;
  }
}

function isSpine4(runtime: string | undefined) {
  return typeof runtime === 'string' && /^4\./.test(runtime);
}

function isSpine38(runtime: string | undefined) {
  return runtime === '3.8';
}

function runtimeFamily(runtime: string | undefined) {
  if (isSpine38(runtime)) return 'spine38';
  if (isSpine4(runtime)) return 'pixi7-4';
  if (runtime === '3.7') return 'pixi6-3.7';
  return 'pixi5-legacy';
}

const PORTRAIT_LAYOUT_SCRIPT = '/hero-portrait-layout.js?v=banner-face-32';

/** 各游戏 Banner / 图鉴主用的 Spine runtime（用于 preload）。 */
export function defaultSpineRuntimeForGame(gameKey: string): SpineRuntime {
  switch (gameKey) {
    case 'lostsword':
      return '4.1';
    case 'guardian-tales':
      return '3.8';
    case 'arcanatactics':
    default:
      return '3.7';
  }
}

/**
 * 指定 runtime 需要的 vendor 脚本 / 样式（相对 public 路径）。
 * 供 SSR `<link rel="preload">` 与引擎加载共用，避免路径漂移。
 */
export function spineRuntimeVendorUrls(runtime: SpineRuntime): {
  scripts: string[];
  styles: string[];
} {
  if (runtime === '3.8') {
    return {
      scripts: ['/vendor/spine-3.8/spine-player.js?v=camera-scale-8'],
      styles: ['/vendor/spine-3.8/spine-player.css'],
    };
  }
  if (isSpine4(runtime)) {
    return {
      scripts: [
        '/vendor/pixi7.min.js',
        '/vendor/spine-pixi-4.1.js',
        PORTRAIT_LAYOUT_SCRIPT,
      ],
      styles: [],
    };
  }
  if (runtime === '3.7') {
    return {
      scripts: [
        '/vendor/pixi6.min.js?v=6.5.10',
        '/vendor/pixi-spine3.js?v=3.0.1',
        PORTRAIT_LAYOUT_SCRIPT,
      ],
      styles: [],
    };
  }
  return {
    scripts: [
      '/vendor/pixi5.min.js',
      `/vendor/pixi-spine-${runtime}.js`,
      PORTRAIT_LAYOUT_SCRIPT,
    ],
    styles: [],
  };
}

/** PIXI.Assets 稳定别名：同 URL 可命中缓存（勿用 Date.now）。 */
function pixiAssetAlias(kind: 'skel' | 'atlas', src: string) {
  return `hero-${kind}:${src}`;
}

const spinePrefetchStarted = new Set<string>();

function toFetchUrl(url: string) {
  if (url.startsWith('//')) return `${location.protocol}${url}`;
  return url;
}

function atlasTextureUrls(atlasUrl: string, atlasText: string): string[] {
  const base = atlasUrl.replace(/[^/?#]+(?:\?[^#]*)?(?:#.*)?$/, '');
  const urls: string[] = [];
  for (const line of atlasText.split(/\r?\n/)) {
    const name = line.trim();
    if (!name || name.includes(':')) continue;
    if (!/\.(?:png|jpe?g|webp)$/i.test(name)) continue;
    if (/^(?:https?:)?\/\//i.test(name) || name.startsWith('/')) {
      urls.push(name);
    } else {
      urls.push(`${base}${name}`);
    }
  }
  return urls;
}

function warmFetch(url: string) {
  const href = toFetchUrl(url);
  if (!href || spinePrefetchStarted.has(href)) return;
  spinePrefetchStarted.add(href);
  void fetch(href, { credentials: 'omit', mode: 'cors', cache: 'force-cache' }).catch(
    () => {
      /* ignore prefetch errors */
    },
  );
}

/**
 * 预取骨骼 / atlas / 贴图到浏览器缓存（选择器 hover 时调用）。
 * 不创建 PIXI 实例，硬切后 Loader 可直接命中 HTTP cache。
 */
export function prefetchSpineAsset(
  asset: Pick<SpinePlayerAsset, 'json' | 'skel' | 'atlas'>,
  cdnBase = '',
) {
  if (typeof window === 'undefined' || !asset?.atlas) return;
  const base = cdnBase.replace(/\/$/, '');
  const skeleton = catalogAssetUrl(asset.skel || asset.json, base);
  const atlas = catalogAssetUrl(asset.atlas, base);
  if (skeleton) warmFetch(skeleton);
  if (!atlas) return;
  const atlasHref = toFetchUrl(atlas);
  if (spinePrefetchStarted.has(`atlas-parsed:${atlasHref}`)) return;
  spinePrefetchStarted.add(`atlas-parsed:${atlasHref}`);
  void fetch(atlasHref, { credentials: 'omit', mode: 'cors', cache: 'force-cache' })
    .then(async (response) => {
      if (!response.ok) return;
      const text = await response.text();
      for (const texture of atlasTextureUrls(atlasHref, text)) {
        warmFetch(texture);
      }
    })
    .catch(() => {
      /* ignore */
    });
}

/** 按顺序预热 runtime 脚本（保持 PIXI → spine 依赖顺序）。 */
export function warmSpineRuntime(runtime: SpineRuntime) {
  if (typeof window === 'undefined') return;
  const { scripts, styles } = spineRuntimeVendorUrls(runtime);
  void (async () => {
    await Promise.all(styles.map((href) => loadStylesheet(href).catch(() => {})));
    for (const src of scripts) {
      await loadScript(src).catch(() => {});
    }
  })();
}

function registerPixiAsset(alias: string, src: string) {
  const assets = window.PIXI?.Assets;
  if (!assets || !src) return;
  try {
    if (assets.cache?.has?.(alias)) return;
    if (assets.resolver?.hasKey?.(alias)) return;
  } catch {
    /* older PIXI */
  }
  try {
    assets.add(alias, src);
  } catch {
    /* already registered */
  }
}

export class SpineEngine {
  private host: HTMLElement;
  private callbacks: SpineEngineCallbacks;
  private catalogCdnBase = '';
  private asset: SpinePlayerAsset | null = null;
  private app: any = null;
  private figure: any = null;
  private loader: any = null;
  private animation = '';
  private active = false;
  private destroyed = false;
  private failed = false;
  private initialized = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private bounds: any = null;
  private portraitBounds: any = null;
  private torsoBoneX: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private spine38Host: HTMLElement | null = null;

  constructor(host: HTMLElement, callbacks: SpineEngineCallbacks = {}) {
    this.host = host;
    this.callbacks = callbacks;
    activeEngines.add(this);
  }

  async start(options: {
    asset: SpinePlayerAsset;
    animation?: string;
    active?: boolean;
    cdnBase?: string;
  }) {
    if (this.initialized || this.destroyed) return;
    this.initialized = true;
    this.catalogCdnBase = (options.cdnBase || '').replace(/\/$/, '');
    this.asset = { ...options.asset };
    for (const key of ['json', 'skel', 'atlas'] as const) {
      const value = catalogAssetUrl(this.asset[key], this.catalogCdnBase);
      if (value) this.asset[key] = value;
    }
    this.active = !!options.active;
    this.animation = options.animation || '';

    const skeleton = this.asset.skel || this.asset.json;
    const runtimeOk =
      ['1.6.2', '2.0.5', '3.7', '3.8'].includes(this.asset.runtime) ||
      isSpine4(this.asset.runtime);
    if (
      !skeleton ||
      !allowed(skeleton, this.catalogCdnBase) ||
      !allowed(this.asset.atlas, this.catalogCdnBase) ||
      !runtimeOk
    ) {
      this.fail();
      return;
    }

    // Drop peers that need a conflicting global PIXI / spine build.
    const family = runtimeFamily(this.asset.runtime);
    for (const peer of [...activeEngines]) {
      if (peer === this || peer.destroyed) continue;
      if (runtimeFamily(peer.asset?.runtime) !== family) {
        peer.destroy();
      }
    }

    this.timer = setTimeout(() => this.fail(), 20000);
    this.wireHostResize();
    document.addEventListener('visibilitychange', this.onVisibility);

    try {
      if (isSpine38(this.asset.runtime)) {
        await this.initSpine38();
        return;
      }
      if (isSpine4(this.asset.runtime)) {
        await this.initSpine4();
        return;
      }
      await this.initLegacyPixiSpine();
    } catch (error) {
      console.error('Spine init failed', error);
      this.fail();
    }
  }

  setAnimation(animation: string) {
    this.animation = animation || '';
    this.play();
  }

  setActive(active: boolean) {
    this.active = !!active;
    this.sync();
    if (this.asset?.coverHeight && !this.active && this.app?.renderer) {
      try {
        this.app.renderer.render(this.app.stage);
      } catch {
        /* ignore */
      }
    }
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    activeEngines.delete(this);
    if (this.timer) clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    if (isSpine38(this.asset?.runtime)) {
      try {
        this.figure?.dispose?.();
      } catch {
        /* ignore */
      }
      this.figure = null;
      this.spine38Host?.remove();
      this.spine38Host = null;
      this.host.replaceChildren();
      return;
    }

    try {
      this.loader?.reset?.();
    } catch {
      /* ignore */
    }
    const dying = this.app;
    this.app = null;
    this.figure = null;
    try {
      dying?.destroy?.(true, { children: true, texture: true, baseTexture: true });
    } catch {
      /* ignore */
    }
    this.host.replaceChildren();
  }

  private size() {
    const width = Math.max(1, this.host.clientWidth || 1);
    const height = Math.max(1, this.host.clientHeight || 1);
    return { width, height };
  }

  private notifyReady() {
    this.callbacks.onReady?.();
  }

  private notifyAnimations(animations: string[]) {
    this.callbacks.onAnimations?.(animations);
  }

  private fail() {
    if (this.failed || this.destroyed) return;
    this.failed = true;
    if (this.timer) clearTimeout(this.timer);
    try {
      if (this.app?.ticker) this.app.stop();
    } catch {
      /* ignore */
    }
    this.callbacks.onError?.();
  }

  private onVisibility = () => this.sync();

  private wireHostResize() {
    this.resizeObserver = new ResizeObserver(() => this.fit());
    this.resizeObserver.observe(this.host);
  }

  private canvasEl() {
    return this.app?.canvas || this.app?.view || null;
  }

  private suppress() {
    if (!this.asset || !this.figure?.skeleton) return;
    for (const name of this.asset.hiddenSlots || []) {
      this.figure.skeleton.findSlot(name)?.setAttachment(null);
    }
  }

  private animationList(target: any) {
    const fromSpineData = target?.spineData?.animations;
    if (Array.isArray(fromSpineData) && fromSpineData.length) {
      return fromSpineData.map((item: { name: string }) => item.name);
    }
    const fromSkeleton = target?.skeleton?.data?.animations;
    if (Array.isArray(fromSkeleton) && fromSkeleton.length) {
      return fromSkeleton.map((item: { name: string }) => item.name);
    }
    return [] as string[];
  }

  private applyPreferredSkin(target: any) {
    const skeleton = target?.skeleton;
    if (!skeleton?.data?.skins?.length) return;
    const skins = [...skeleton.data.skins];
    const preferred = typeof this.asset?.skin === 'string' ? this.asset.skin : '';
    let chosen = preferred
      ? skins.find((item: { name?: string }) => item?.name === preferred)
      : null;
    if (!chosen) {
      chosen = [...skins].sort((a: any, b: any) => {
        const count = (value: any) =>
          value?.attachments ? Object.keys(value.attachments).length : 0;
        return count(b) - count(a);
      })[0];
    }
    if (!chosen?.name) return;
    try {
      if (typeof skeleton.setSkinByName === 'function') {
        skeleton.setSkinByName(chosen.name);
      } else if (typeof skeleton.setSkin === 'function') {
        skeleton.setSkin(chosen);
      }
      if (typeof skeleton.setSlotsToSetupPose === 'function') {
        skeleton.setSlotsToSetupPose();
      }
    } catch {
      /* keep default skin */
    }
  }

  private play() {
    if (!this.figure || !this.asset) return;
    if (this.asset.runtime === '3.8') {
      const names = (this.figure.skeleton?.data?.animations || []).map(
        (item: { name: string }) => item.name,
      );
      const chosen = names.includes(this.animation)
        ? this.animation
        : names.find((name: string) => /^(idle|wait|stand)$/i.test(name)) ||
          names.find((name: string) => /idle|wait|stand|default/i.test(name)) ||
          names[0];
      if (chosen && this.host.dataset.animation !== chosen) {
        this.figure.setAnimation(chosen);
      }
      this.host.dataset.animation = chosen || '';
      return;
    }
    const names = this.animationList(this.figure);
    const chosen = names.includes(this.animation)
      ? this.animation
      : names.find((name) => /^(idle|wait|stand)$/i.test(name)) || names[0];
    if (
      chosen &&
      this.figure.state.getCurrent(0)?.animation?.name !== chosen
    ) {
      this.figure.state.setAnimation(0, chosen, true);
    }
    this.host.dataset.animation = chosen || '';
  }

  private findBone(skeleton: any, names: string[]) {
    if (!skeleton || typeof skeleton.findBone !== 'function') return null;
    for (const name of names) {
      const bone = skeleton.findBone(name);
      if (bone) return bone;
    }
    return null;
  }

  private detectFaceDir(skeleton: any): 'left' | 'right' {
    if (!skeleton) return 'left';
    try {
      if (typeof skeleton.updateWorldTransform === 'function') {
        skeleton.updateWorldTransform();
      }
      const nose = this.findBone(skeleton, ['Nose', 'nose']);
      const head = this.findBone(skeleton, [
        'Face',
        'face',
        'head',
        'Head',
        'Neck',
        'neck',
        'Chest_Cntr',
        'chest',
        'Pelvis',
        'pelvis',
      ]);
      if (
        nose &&
        head &&
        nose !== head &&
        Number.isFinite(nose.worldX) &&
        Number.isFinite(head.worldX)
      ) {
        if (Math.abs(nose.worldX - head.worldX) > 4) {
          return nose.worldX >= head.worldX ? 'right' : 'left';
        }
      }
      const headF = this.findBone(skeleton, [
        'head_F',
        'head_f',
        'Head_F',
        'face_F',
        'Face_F',
      ]);
      const headB = this.findBone(skeleton, [
        'head_B',
        'head_b',
        'Head_B',
        'face_B',
        'Face_B',
      ]);
      if (
        headF &&
        headB &&
        Number.isFinite(headF.worldX) &&
        Number.isFinite(headB.worldX)
      ) {
        const dx = headF.worldX - headB.worldX;
        if (Math.abs(dx) > 4) return dx >= 0 ? 'right' : 'left';
      }
      const faceBone = this.findBone(skeleton, [
        'face',
        'Face',
        'head',
        'Head',
      ]);
      if (faceBone && Number.isFinite(faceBone.worldX)) {
        let hatSum = 0;
        let hatN = 0;
        for (const bone of skeleton.bones || []) {
          const name = (bone.data && bone.data.name) || bone.name || '';
          if (!/^(head\d+|hat)/i.test(name) || /^head$/i.test(name)) continue;
          if (!Number.isFinite(bone.worldX)) continue;
          hatSum += bone.worldX;
          hatN += 1;
        }
        if (hatN >= 3) {
          const hatX = hatSum / hatN;
          if (Math.abs(faceBone.worldX - hatX) > 30) {
            return faceBone.worldX >= hatX ? 'right' : 'left';
          }
        }
      }
      let faceSum = 0;
      let faceN = 0;
      let bodySum = 0;
      let bodyN = 0;
      for (const slot of skeleton.slots || []) {
        const att =
          typeof slot.getAttachment === 'function'
            ? slot.getAttachment()
            : null;
        if (!att || typeof att.computeWorldVertices !== 'function') continue;
        const name = (slot.data && slot.data.name) || '';
        const isFace = /face|eye|head|nose|口|眼|头|脸|前发|刘海/i.test(name);
        const world: number[] = [];
        try {
          const len = att.worldVerticesLength || 0;
          if (len > 0) att.computeWorldVertices(slot, 0, len, world, 0, 2);
          else continue;
        } catch {
          continue;
        }
        for (let j = 0; j < world.length; j += 2) {
          const x = world[j];
          if (!Number.isFinite(x)) continue;
          if (isFace) {
            faceSum += x;
            faceN += 1;
          } else {
            bodySum += x;
            bodyN += 1;
          }
        }
      }
      if (faceN > 8 && bodyN > 8) {
        return faceSum / faceN >= bodySum / bodyN ? 'right' : 'left';
      }
    } catch {
      /* ignore */
    }
    return 'left';
  }

  private shouldMirrorToCenter() {
    if (
      !this.asset?.bannerSide ||
      this.asset.bannerSide === 'center' ||
      !this.figure?.skeleton
    ) {
      return false;
    }
    const want = this.asset.bannerSide === 'right' ? 'left' : 'right';
    return this.detectFaceDir(this.figure.skeleton) !== want;
  }

  private torsoAnchorX(skeleton: any) {
    if (!skeleton) return null;
    try {
      if (typeof skeleton.updateWorldTransform === 'function') {
        skeleton.updateWorldTransform();
      }
      const bone = this.findBone(skeleton, [
        'Body',
        'body',
        'Chest_Cntr',
        'Pelvis',
        'pelvis',
        'Spine2',
        'Spine1',
        'Chest',
        'chest',
        'Face',
        'face',
        'Waist',
        'waist',
      ]);
      if (bone && Number.isFinite(bone.worldX)) return bone.worldX as number;
    } catch {
      /* ignore */
    }
    return null;
  }

  private fit() {
    if (this.destroyed || this.failed || !this.asset) return;
    if (isSpine38(this.asset.runtime)) return;
    if (!this.figure || !this.app) return;
    const { width: innerWidth, height: innerHeight } = this.size();
    const portraitFrame = this.host.closest('.spine-portrait');
    const overscanTop = portraitFrame
      ? Math.max(
          0,
          Math.min(
            innerHeight - 1,
            portraitFrame.getBoundingClientRect().top -
              this.host.getBoundingClientRect().top,
          ),
        )
      : 0;
    // Render above the portrait frame without changing its original camera fit.
    const frameHeight = innerHeight - overscanTop;
    this.app.renderer.resize(innerWidth, innerHeight);
    if (this.asset.coverHeight) {
      const scaleBox = this.portraitBounds?.body || this.bounds;
      const alignXBox = this.portraitBounds?.core || scaleBox;
      const side = this.asset.bannerSide;
      const scale =
        (frameHeight / Math.max(scaleBox.height, 1)) * (this.asset.scale || 0.9);
      let anchorX = innerWidth * 0.5;
      if (side === 'left' || side === 'right') {
        const baseOuter = this.asset.bannerInnerRatio ?? 0.5;
        const wideFactor = (this.bounds.width * scale) / Math.max(innerWidth, 1);
        let outerWeight = baseOuter;
        if (wideFactor > 1.02) {
          const t = Math.min(1, (wideFactor - 1.02) / 0.7);
          outerWeight =
            baseOuter +
            t * ((this.asset.bannerOuterRatio ?? 0.78) - baseOuter);
        }
        anchorX =
          side === 'left'
            ? innerWidth * (1 - outerWeight)
            : innerWidth * outerWeight;
      }
      const coreCx = alignXBox.x + alignXBox.width / 2;
      const boneX = Number.isFinite(this.torsoBoneX as number)
        ? this.torsoBoneX
        : this.torsoAnchorX(this.figure.skeleton);
      const cx = this.portraitBounds?.core
        ? coreCx
        : Number.isFinite(boneX as number)
          ? (boneX as number)
          : coreCx;
      const mirror = this.shouldMirrorToCenter();
      const x = mirror
        ? anchorX +
          cx * scale +
          innerWidth * (this.asset.framing?.offsetX || 0)
        : anchorX -
          cx * scale +
          innerWidth * (this.asset.framing?.offsetX || 0);
      const y =
        side === 'center'
          ? frameHeight -
            (scaleBox.y + scaleBox.height) * scale +
            frameHeight * (this.asset.framing?.offsetY || 0)
          : frameHeight / 2 -
            (scaleBox.y + scaleBox.height / 2) * scale +
            frameHeight * (this.asset.framing?.offsetY || 0);
      this.figure.scale.set(mirror ? -scale : scale, scale);
      this.figure.position.set(x, y + overscanTop);
      this.app.renderer.render(this.app.stage);
      return;
    }
    if (this.asset.framing && window.HeroPortraitLayout) {
      const framed = window.HeroPortraitLayout.layout(
        innerWidth,
        frameHeight,
        this.bounds,
        this.portraitBounds,
        this.asset.framing,
      );
      this.figure.scale.set(framed.scale);
      this.figure.position.set(framed.x, framed.y + overscanTop);
      this.app.renderer.render(this.app.stage);
      return;
    }
    const referenceHeight =
      Number.isFinite(this.asset.referenceHeight) &&
      (this.asset.referenceHeight as number) > 0
        ? (this.asset.referenceHeight as number)
        : 1;
    const scale =
      Math.min(
        innerWidth / Math.max(this.bounds.width, 1),
        frameHeight / Math.max(this.bounds.height, referenceHeight),
      ) * (this.asset.scale || 0.8);
    this.figure.scale.set(scale);
    this.figure.position.set(
      innerWidth / 2 - (this.bounds.x + this.bounds.width / 2) * scale,
      frameHeight / 2 - (this.bounds.y + this.bounds.height / 2) * scale + overscanTop,
    );
    this.app.renderer.render(this.app.stage);
  }

  private sync() {
    if (this.destroyed || this.failed || !this.asset) return;
    if (isSpine38(this.asset.runtime) && this.figure) {
      try {
        if (this.active && !document.hidden) this.figure.play();
        else this.figure.pause();
      } catch {
        /* player may still be initializing */
      }
      this.host.dataset.playing = String(
        this.active && !document.hidden && !this.failed,
      );
      return;
    }
    if (!this.app) return;
    try {
      if (this.active && !document.hidden) this.app.start();
      else if (this.app.ticker) this.app.stop();
    } catch {
      /* app may already be tearing down */
    }
    this.host.dataset.playing = String(
      this.active && !document.hidden && !this.failed,
    );
  }

  private freezeAtStart() {
    if (!this.figure || !this.app || this.failed || this.destroyed) return;
    try {
      this.play();
      const entry =
        this.figure.state && typeof this.figure.state.getCurrent === 'function'
          ? this.figure.state.getCurrent(0)
          : null;
      if (entry) {
        entry.trackTime = 0;
        if ('animationLast' in entry) entry.animationLast = -1;
        if ('trackLast' in entry) entry.trackLast = -1;
        if ('nextTrackLast' in entry) entry.nextTrackLast = -1;
      }
      this.figure.update(0);
      this.suppress();
      if (this.app.renderer) this.app.renderer.render(this.app.stage);
    } catch {
      /* ignore */
    }
  }

  private measurePortrait() {
    this.bounds = this.figure.getLocalBounds().clone();
    this.torsoBoneX = this.torsoAnchorX(this.figure.skeleton);
    if (
      !(this.asset?.framing || this.asset?.coverHeight) ||
      typeof window.HeroPortraitLayout === 'undefined'
    ) {
      return;
    }
    const slots = (this.figure.skeleton?.slots || [])
      .map((slot: any, i: number) => {
        const container = this.figure.slotContainers?.[i];
        if (!container) return null;
        return container.visible && slot.color.a > 0 && slot.getAttachment()
          ? { name: slot.data.name, bounds: container.getBounds().clone() }
          : null;
      })
      .filter(Boolean);
    this.portraitBounds = slots.length
      ? window.HeroPortraitLayout.measure(slots, this.bounds)
      : null;
  }

  private wireTickerAndReady() {
    this.app.ticker.add((delta: number) => {
      if (!this.figure || this.destroyed || this.failed) return;
      try {
        this.figure.update(Math.min(delta / 60, 0.05));
        this.suppress();
      } catch {
        /* skip a bad frame */
      }
    });
    if (this.timer) clearTimeout(this.timer);
    if (this.asset?.coverHeight && !this.active) this.freezeAtStart();
    this.sync();
    this.notifyAnimations(this.animationList(this.figure));
    this.notifyReady();
  }

  private async initSpine38() {
    await Promise.all([
      loadStylesheet('/vendor/spine-3.8/spine-player.css'),
      loadScript('/vendor/spine-3.8/spine-player.js?v=camera-scale-8'),
    ]);
    if (this.destroyed || this.failed || !window.spine?.SpinePlayer) {
      this.fail();
      return;
    }
    const host = document.createElement('div');
    host.style.cssText = 'width:100%;height:100%;';
    this.host.appendChild(host);
    this.spine38Host = host;
    this.figure = new window.spine.SpinePlayer(host, {
      skelUrl: this.asset!.skel || this.asset!.json,
      atlasUrl: this.asset!.coverHeight
        ? `${this.asset!.atlas}?v=pma-linear-filter-3`
        : this.asset!.atlas,
      showControls: false,
      alpha: true,
      premultipliedAlpha: true,
      displayScale: this.asset!.coverHeight ? (this.asset!.scale ?? 1) : 1,
      backgroundColor: '#00000000',
      viewport: this.asset!.coverHeight
        ? {
            padLeft: '0%',
            padRight: '0%',
            padTop: '8%',
            padBottom: '6%',
          }
        : {
            padLeft: '8%',
            padRight: '8%',
            padTop: '8%',
            padBottom: '8%',
          },
      success: (player: any) => {
        if (this.destroyed || this.failed) return;
        this.figure = player;
        if (player.playerControls) {
          player.playerControls.style.setProperty('display', 'none', 'important');
        }
        player.dom
          ?.querySelectorAll?.(
            '.spine-player-controls, .spine-player-popup',
          )
          .forEach((element: HTMLElement) =>
            element.style.setProperty('display', 'none', 'important'),
          );
        const animations = (player.skeleton?.data?.animations || [])
          .map((item: { name: string }) => item.name)
          .filter(Boolean);
        const skins = player.skeleton?.data?.skins || [];
        const skin = [...skins].sort((a: any, b: any) => {
          const count = (value: any) =>
            value?.attachments ? Object.keys(value.attachments).length : 0;
          return count(b) - count(a);
        })[0];
        if (skin?.name) {
          try {
            player.skeleton.setSkinByName(skin.name);
            player.skeleton.setSlotsToSetupPose();
          } catch {
            /* use source default */
          }
        }
        this.play();
        this.sync();
        if (this.timer) clearTimeout(this.timer);
        this.notifyAnimations(animations);
        this.notifyReady();
      },
      error: () => this.fail(),
    });
  }

  private async initSpine4() {
    await loadScript('/vendor/pixi7.min.js');
    const spineLoad = loadScript('/vendor/spine-pixi-4.1.js');
    const layoutLoad =
      this.asset?.framing || this.asset?.coverHeight
        ? loadScript(PORTRAIT_LAYOUT_SCRIPT)
        : Promise.resolve();
    await Promise.all([spineLoad, layoutLoad]);
    if (this.destroyed || this.failed) return;
    if (
      typeof window.PIXI === 'undefined' ||
      typeof window.spine === 'undefined' ||
      typeof window.spine.Spine?.from !== 'function'
    ) {
      this.fail();
      return;
    }
    const { width, height } = this.size();
    this.app = new window.PIXI.Application({
      width,
      height,
      backgroundAlpha: 0,
      antialias: true,
      autoDensity: true,
      autoStart: false,
      resolution: Math.min(devicePixelRatio || 1, 2),
    });
    const view = this.canvasEl();
    if (view) this.host.appendChild(view);
    const skelSrc = this.asset!.json!;
    const atlasSrc = this.asset!.atlas;
    const skelAlias = pixiAssetAlias('skel', skelSrc);
    const atlasAlias = pixiAssetAlias('atlas', atlasSrc);
    registerPixiAsset(skelAlias, skelSrc);
    registerPixiAsset(atlasAlias, atlasSrc);
    await window.PIXI.Assets.load([skelAlias, atlasAlias]);
    if (this.destroyed || this.failed) return;
    this.figure = window.spine.Spine.from(skelAlias, atlasAlias, {
      autoUpdate: false,
    });
    this.app.stage.addChild(this.figure);
    this.applyPreferredSkin(this.figure);
    this.play();
    if (typeof this.figure.update === 'function') this.figure.update(0);
    this.suppress();
    this.measurePortrait();
    this.fit();
    this.wireTickerAndReady();
  }

  private async initLegacyPixiSpine() {
    const runtime = this.asset!.runtime;
    if (runtime === '3.7') {
      if (!this.asset!.json) throw new Error('Spine 3.7 需要 JSON 骨骼文件');
      await loadScript('/vendor/pixi6.min.js?v=6.5.10');
      const spineLoad = loadScript('/vendor/pixi-spine3.js?v=3.0.1');
      const layoutLoad =
        this.asset?.framing || this.asset?.coverHeight
          ? loadScript(PORTRAIT_LAYOUT_SCRIPT)
          : Promise.resolve();
      await Promise.all([spineLoad, layoutLoad]);
    } else {
      await loadScript('/vendor/pixi5.min.js');
      const spineLoad = loadScript(`/vendor/pixi-spine-${runtime}.js`);
      const layoutLoad =
        this.asset?.framing || this.asset?.coverHeight
          ? loadScript(PORTRAIT_LAYOUT_SCRIPT)
          : Promise.resolve();
      await Promise.all([spineLoad, layoutLoad]);
    }
    if (this.destroyed || this.failed) return;
    if (typeof window.PIXI === 'undefined') {
      this.fail();
      return;
    }

    const { width, height } = this.size();
    this.app = new window.PIXI.Application({
      width,
      height,
      transparent: true,
      antialias: true,
      autoDensity: true,
      autoStart: false,
      resolution: Math.min(devicePixelRatio || 1, 2),
    });
    this.host.appendChild(this.app.view);

    const skeleton = this.asset!.skel || this.asset!.json;
    const mount = (spineData: any) => {
      if (this.destroyed || this.failed) return;
      try {
        if (!spineData) return this.fail();
        const SpineClass = window.PIXI.spine.Spine;
        this.figure = new SpineClass(spineData);
        this.figure.autoUpdate = false;
        this.app.stage.addChild(this.figure);
        this.applyPreferredSkin(this.figure);
        this.play();
        this.figure.update(0);
        this.suppress();
        this.bounds = this.figure.getLocalBounds().clone();
        this.torsoBoneX = this.torsoAnchorX(this.figure.skeleton);
        if (this.asset?.framing || this.asset?.coverHeight) {
          const slots = this.figure.skeleton.slots
            .map((slot: any, i: number) => {
              const container = this.figure.slotContainers[i];
              return container?.visible &&
                slot.color.a > 0 &&
                slot.getAttachment()
                ? {
                    name: slot.data.name,
                    bounds: container.getBounds().clone(),
                  }
                : null;
            })
            .filter(Boolean);
          this.portraitBounds = window.HeroPortraitLayout?.measure(
            slots,
            this.bounds,
          );
        }
        this.fit();
        this.app.ticker.add((delta: number) => {
          if (!this.figure || this.destroyed || this.failed) return;
          try {
            this.figure.update(Math.min(delta / 60, 0.05));
            this.suppress();
          } catch {
            /* skip */
          }
        });
        if (this.timer) clearTimeout(this.timer);
        if (this.asset?.coverHeight && !this.active) this.freezeAtStart();
        this.sync();
        if (this.app.renderer) this.app.renderer.render(this.app.stage);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (!this.destroyed && !this.failed) {
              this.notifyAnimations(this.animationList(this.figure));
              this.notifyReady();
            }
          }),
        );
      } catch (error) {
        console.error('Spine mount failed', error);
        this.fail();
      }
    };

    this.loader = new window.PIXI.Loader();
    this.loader.onError.add(() => this.fail());
    this.loader
      .add('hero', skeleton, { metadata: { spineAtlasFile: this.asset!.atlas } })
      .load((_: unknown, resources: any) => {
        mount(resources.hero?.spineData);
      });
  }
}
